// Analytics tracking utility
import axios from 'axios';
import { BACKEND_URL, API_URL } from './api';

const API = API_URL;

// Cookie helpers
const setCookie = (name, value, days) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

// Check if user has accepted cookies
export const hasCookieConsent = () => {
  const consent = getCookie('cookie_consent');
  // Only track with session if explicitly accepted
  return consent === 'true';
};

// Check if user has explicitly declined
export const hasDeclinedCookies = () => {
  return getCookie('cookie_consent') === 'false';
};

// Set cookie consent
export const setCookieConsent = (accepted) => {
  setCookie('cookie_consent', accepted ? 'true' : 'false', 365);
  
  // If accepted, generate session ID immediately
  if (accepted) {
    getSessionId();
  }
};

// Generate UUID for session
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Get or create session ID (only if consent is given)
const getSessionId = () => {
  // Only generate/return session ID if user has accepted cookies
  if (!hasCookieConsent()) {
    return null;
  }
  
  let sessionId = getCookie('session_id');
  if (!sessionId) {
    sessionId = generateUUID();
    setCookie('session_id', sessionId, 30); // 30 days
  }
  return sessionId;
};

// Track event
export const trackEvent = async (eventType, page, element = null) => {
  try {
    const sessionId = getSessionId(); // Will be null if no consent
    const referrer = document.referrer || null;
    const userAgent = navigator.userAgent;

    await axios.post(`${API}/analytics/track`, {
      event_type: eventType,
      page: page || window.location.pathname,
      element,
      session_id: sessionId, // Can be null - backend should handle this
      referrer,
      user_agent: userAgent
    });
  } catch (error) {
    // Silently fail - don't disrupt user experience
    console.debug('Analytics tracking failed:', error);
  }
};

// Track event with view_id for deduplication
const trackEventWithViewId = async (eventType, page, element = null, viewId = null) => {
  try {
    const sessionId = getSessionId();
    const referrer = document.referrer || null;
    const userAgent = navigator.userAgent;

    await axios.post(`${API}/analytics/track`, {
      event_type: eventType,
      page: page || window.location.pathname,
      element,
      session_id: sessionId,
      view_id: viewId,
      referrer,
      user_agent: userAgent
    });
  } catch (error) {
    console.debug('Analytics tracking failed:', error);
  }
};

// Track section engagement with continuous timers and visibility weighting
let sectionStates = {}; // Per-section state: { spans: [], lastEntryTime, lastRatio, timer, flushed }
let isUserActive = true;
let lastActivityTime = Date.now();
let activityCheckInterval = null;

// Activity detection threshold (60 seconds of inactivity)
const INACTIVITY_THRESHOLD = 60000;
// Minimum time to accumulate before sending (2 seconds)
const MIN_FLUSH_TIME = 2000;

// User activity listeners
const setupActivityDetection = () => {
  const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
  
  const onActivity = () => {
    const wasInactive = !isUserActive;
    isUserActive = true;
    lastActivityTime = Date.now();
    
    // If user was inactive and is now active, flush all sections
    if (wasInactive) {
      flushAllSections('activity_resume');
    }
  };
  
  activityEvents.forEach(event => {
    document.addEventListener(event, onActivity, { passive: true });
  });
  
  // Check for inactivity every 10 seconds
  activityCheckInterval = setInterval(() => {
    const timeSinceActivity = Date.now() - lastActivityTime;
    if (timeSinceActivity > INACTIVITY_THRESHOLD && isUserActive) {
      isUserActive = false;
      flushAllSections('inactivity');
    }
  }, 10000);
};

// Calculate total weighted dwell time for a section
const calculateWeightedTime = (spans) => {
  return spans.reduce((total, span) => {
    const duration = span.leftAt - span.enteredAt;
    const weighted = duration * span.ratio;
    return total + weighted;
  }, 0);
};

// Flush analytics for a section
const flushSection = (sectionName, reason = 'exit') => {
  const state = sectionStates[sectionName];
  if (!state || state.flushed || state.spans.length === 0) return;
  
  // Calculate total weighted time
  const totalWeightedMs = calculateWeightedTime(state.spans);
  const totalSeconds = Math.floor(totalWeightedMs / 1000);
  
  // Only send if at least 2 seconds accumulated
  if (totalSeconds < 2) return;
  
  // Use requestIdleCallback for mid-session updates, immediate for critical events
  const sendAnalytics = () => {
    trackEventWithViewId(
      'section_view',
      window.location.pathname,
      `${sectionName}:${totalSeconds}s`,
      state.viewId
    );
  };
  
  if (reason === 'unload' || reason === 'visibility_change') {
    // Critical: send immediately with sendBeacon
    sendAnalytics();
    state.flushed = true;
  } else {
    // Non-critical: use idle callback
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(() => sendAnalytics(), { timeout: 2000 });
    } else {
      setTimeout(() => sendAnalytics(), 100);
    }
  }
};

// Flush all sections
const flushAllSections = (reason) => {
  Object.keys(sectionStates).forEach(sectionName => {
    // Close any open spans
    const state = sectionStates[sectionName];
    if (state.lastEntryTime && !state.flushed) {
      state.spans.push({
        enteredAt: state.lastEntryTime,
        leftAt: Date.now(),
        ratio: state.lastRatio || 1.0
      });
      state.lastEntryTime = null;
    }
    flushSection(sectionName, reason);
  });
};

// Track section entry
export const trackSectionView = (sectionName) => {
  if (!sectionStates[sectionName]) {
    sectionStates[sectionName] = {
      spans: [],
      lastEntryTime: null,
      lastRatio: 0,
      timer: null,
      flushed: false,
      viewId: generateUUID()
    };
  }
  
  const state = sectionStates[sectionName];
  
  // If re-entering after exit, allow accumulation
  if (state.flushed) {
    // Reset for new view
    state.spans = [];
    state.flushed = false;
    state.viewId = generateUUID();
  }
  
  state.lastEntryTime = Date.now();
};

// Track section exit with optional intersection ratio
export const trackSectionExit = (sectionName, finalRatio = null) => {
  const state = sectionStates[sectionName];
  if (!state || !state.lastEntryTime) return;
  
  // Close current span
  state.spans.push({
    enteredAt: state.lastEntryTime,
    leftAt: Date.now(),
    ratio: finalRatio !== null ? finalRatio : state.lastRatio
  });
  
  state.lastEntryTime = null;
  
  // Check if we should flush
  const totalWeightedMs = calculateWeightedTime(state.spans);
  if (totalWeightedMs >= MIN_FLUSH_TIME) {
    flushSection(sectionName, 'exit');
  }
};

// Update visibility ratio for currently viewed section
export const updateSectionRatio = (sectionName, ratio) => {
  const state = sectionStates[sectionName];
  if (!state) return;
  
  state.lastRatio = ratio;
  
  // If section is currently being viewed, close old span and start new one
  if (state.lastEntryTime) {
    const now = Date.now();
    state.spans.push({
      enteredAt: state.lastEntryTime,
      leftAt: now,
      ratio: state.lastRatio
    });
    state.lastEntryTime = now;
    
    // Check if we should flush
    const totalWeightedMs = calculateWeightedTime(state.spans);
    if (totalWeightedMs >= MIN_FLUSH_TIME && isUserActive) {
      flushSection(sectionName, 'ratio_update');
      // Reset after flush but keep viewing
      state.spans = [];
      state.flushed = false;
    }
  }
};

// Initialize activity detection and unload handlers
if (typeof window !== 'undefined') {
  // Setup activity detection
  setupActivityDetection();
  
  // Track all active sections when page is about to unload
  window.addEventListener('beforeunload', () => {
    flushAllSections('unload');
    
    // Use sendBeacon for final critical data
    Object.keys(sectionStates).forEach(sectionName => {
      const state = sectionStates[sectionName];
      if (state.flushed) return;
      
      const totalWeightedMs = calculateWeightedTime(state.spans);
      const totalSeconds = Math.floor(totalWeightedMs / 1000);
      
      if (totalSeconds >= 2) {
        const sessionId = getSessionId();
        const data = {
          event_type: 'section_view',
          page: window.location.pathname,
          element: `${sectionName}:${totalSeconds}s`,
          session_id: sessionId,
          view_id: state.viewId,
          referrer: document.referrer || null,
          user_agent: navigator.userAgent
        };
        
        const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
        navigator.sendBeacon(`${API}/analytics/track`, blob);
      }
    });
  });
  
  // Track on visibility change (user switches tabs)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      flushAllSections('visibility_change');
    }
  });
}

// Track page view
export const trackPageView = () => {
  trackEvent('page_view', window.location.pathname);
};

// Track button click
export const trackButtonClick = (buttonText) => {
  trackEvent('button_click', window.location.pathname, buttonText);
};

// Track link click
export const trackLinkClick = (linkText, destination) => {
  trackEvent('link_click', window.location.pathname, `${linkText} -> ${destination}`);
};

// Track form submission
export const trackFormSubmit = (formName) => {
  trackEvent('form_submit', window.location.pathname, formName);
};
